/**
 * Admin Overview Tab
 * System health, API status, generation locks, user activity, betting data,
 * settlement pipeline, LLM usage, A/B testing performance, and management actions
 */

import { LoadingSpinner } from '@components/ui';
import { useAuth } from '@contexts/AuthContext';
import { type ApiHealthStatus, useSystemHealth } from '@hooks/useSystemHealth';
import { RefreshCw } from 'lucide-react';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { apiUrl } from '../../config/api';
import { ABTestingSection } from './ABTestingSection';
import { GenerationLocksSection } from './GenerationLocksSection';
import { SettlementMonitorSection } from './SettlementMonitorSection';

// =============================================================================
// Helper Components
// =============================================================================

function ApiHealthCard({ api }: { api: ApiHealthStatus }): ReactElement {
  const statusColors = {
    healthy: 'bg-theme-success',
    degraded: 'bg-theme-warning',
    down: 'bg-theme-danger',
    unknown: 'bg-theme-text-tertiary',
  };

  const statusText = {
    healthy: 'Healthy',
    degraded: 'Degraded',
    down: 'Down',
    unknown: 'Unknown',
  };

  return (
    <div className="bg-theme-bg rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium text-theme-text">{api.name}</span>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${statusColors[api.status]}`} />
          <span
            className={`text-sm ${
              api.status === 'healthy'
                ? 'text-theme-success'
                : api.status === 'degraded'
                  ? 'text-theme-warning'
                  : api.status === 'down'
                    ? 'text-theme-danger'
                    : 'text-theme-text-secondary'
            }`}
          >
            {statusText[api.status]}
          </span>
        </div>
      </div>

      <div className="text-xs text-theme-text-tertiary space-y-1">
        {api.responseTimeMs !== null && <p>Response: {api.responseTimeMs}ms</p>}
        {api.lastSuccessfulFetch && (
          <p>Last success: {new Date(api.lastSuccessfulFetch).toLocaleTimeString()}</p>
        )}
        {api.error && (
          <p className="text-theme-danger truncate" title={api.error}>
            Error: {api.error}
          </p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// OverviewTab
// =============================================================================

export function OverviewTab(): ReactElement {
  const { authFetch } = useAuth();
  const {
    health,
    loading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useSystemHealth();

  const [recomputing, setRecomputing] = useState(false);
  const [recomputeSuccess, setRecomputeSuccess] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState<{
    totalUsers: number;
    successCount: number;
    errorCount: number;
  } | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<{
    globalLegsCount: number;
    dedupedCount: number;
  } | null>(null);

  const formatNumber = (num: number | null): string => {
    if (num === null) return 'N/A';
    return num.toLocaleString();
  };

  const formatPercentage = (num: number): string => {
    return `${num.toFixed(1)}%`;
  };

  const handleRecomputeLeaderboard = async (): Promise<void> => {
    if (
      !confirm('Are you sure you want to recompute all leaderboard stats? This may take a moment.')
    ) {
      return;
    }

    setRecomputing(true);
    setRecomputeSuccess(false);
    setRecomputeResult(null);

    try {
      const response = await authFetch(apiUrl('/api/admin/leaderboard/recompute'), {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to recompute leaderboard');
      }

      const result = await response.json();
      setRecomputeResult(result);
      setRecomputeSuccess(true);
      setTimeout(() => {
        setRecomputeSuccess(false);
        setRecomputeResult(null);
      }, 10000);
    } catch (_err) {
      alert('Failed to recompute leaderboard. Check console for details.');
    } finally {
      setRecomputing(false);
    }
  };

  const handleRecalculateEfficiency = async (): Promise<void> => {
    if (!confirm('This will clear and recalculate all historical efficiency data. Continue?')) {
      return;
    }

    setRecalculating(true);
    setRecalcResult(null);

    try {
      const response = await authFetch(apiUrl('/api/admin/historical-efficiency/recalculate'), {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to recalculate historical efficiency');
      }

      const result = await response.json();
      setRecalcResult(result);
      setTimeout(() => setRecalcResult(null), 10000);
    } catch (_err) {
      alert('Failed to recalculate. Check console for details.');
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* System Status Banner */}
      {health && (
        <div
          className={`rounded-lg p-4 border ${
            health.systemStatus.overall === 'healthy'
              ? 'bg-theme-success/10 border-theme-success/20'
              : health.systemStatus.overall === 'degraded'
                ? 'bg-theme-warning/10 border-theme-warning/20'
                : 'bg-theme-danger/10 border-theme-danger/20'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${
                  health.systemStatus.overall === 'healthy'
                    ? 'bg-theme-success'
                    : health.systemStatus.overall === 'degraded'
                      ? 'bg-theme-warning'
                      : 'bg-theme-danger'
                }`}
              />
              <span
                className="font-medium text-theme-text cursor-help"
                title={
                  health.systemStatus.overall === 'healthy'
                    ? 'All systems operational: Database connected, all APIs responding'
                    : health.systemStatus.overall === 'degraded'
                      ? 'Partial issues: One API down or multiple APIs degraded'
                      : 'Critical issues: Database down or multiple APIs unreachable'
                }
              >
                System Status: <span className="capitalize">{health.systemStatus.overall}</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => refetchHealth()}
              disabled={healthLoading}
              className="text-sm text-theme-text-secondary hover:text-theme-text"
            >
              {healthLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      )}

      {healthError && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4">
          <p className="text-sm text-theme-danger">{healthError}</p>
        </div>
      )}

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
          <p className="text-sm text-theme-text-tertiary">Cache Hit Rate</p>
          <p className="text-2xl font-bold text-theme-success">
            {healthLoading
              ? 'Loading...'
              : health?.systemStatus.cacheHitRate != null
                ? formatPercentage(health.systemStatus.cacheHitRate)
                : 'N/A'}
          </p>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
          <p className="text-sm text-theme-text-tertiary">Odds API Quota</p>
          <p className="text-2xl font-bold text-theme-text">
            {formatNumber(health?.systemStatus.oddsApiQuota ?? null)}
          </p>
        </div>
        <div
          className="bg-theme-surface rounded-lg p-4 border border-theme-border cursor-help"
          title={
            health?.systemStatus.database
              ? 'PostgreSQL database is responding to queries'
              : 'Database connection failed - check server logs'
          }
        >
          <p className="text-sm text-theme-text-tertiary">Database</p>
          <p
            className={`text-2xl font-bold ${health?.systemStatus.database ? 'text-theme-success' : 'text-theme-danger'}`}
          >
            {healthLoading ? '...' : health?.systemStatus.database ? 'Connected' : 'Down'}
          </p>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
          <p className="text-sm text-theme-text-tertiary">Pending Settlement</p>
          <p className="text-2xl font-bold text-theme-warning">
            {healthLoading ? '...' : (health?.settlement.pendingParlays ?? 0)}
          </p>
        </div>
      </div>

      {/* API Health */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">API Health</h2>
          <p className="text-sm text-theme-text-tertiary">External service connectivity status</p>
        </div>

        {healthLoading ? (
          <div className="flex items-center justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4">
            {health?.apiHealth.map((api) => (
              <ApiHealthCard key={api.name} api={api} />
            ))}
          </div>
        )}
      </div>

      {/* Generation Locks */}
      <GenerationLocksSection />

      {/* User & Activity Stats */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">User Activity</h2>
          <p className="text-sm text-theme-text-tertiary">
            User registration and engagement metrics
          </p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-theme-text-secondary mb-2">Active Users</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Total</p>
              <p className="text-2xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.userActivity.totalUsers ?? null)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Active (24h)</p>
              <p className="text-2xl font-bold text-theme-primary">
                {healthLoading ? '...' : formatNumber(health?.userActivity.activeUsers24h ?? null)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Active (7d)</p>
              <p className="text-2xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.userActivity.activeUsers7d ?? null)}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-theme-text-secondary mb-2">New Registrations</h3>
          <div className="grid grid-cols-2 @3xl:grid-cols-5 gap-4">
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Today</p>
              <p className="text-2xl font-bold text-theme-success">
                {healthLoading ? '...' : formatNumber(health?.userActivity.newUsersToday ?? null)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last 7 Days</p>
              <p className="text-2xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.userActivity.newUsersLast7d ?? null)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last 30 Days</p>
              <p className="text-2xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.userActivity.newUsersLast30d ?? null)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last 6 Months</p>
              <p className="text-2xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.userActivity.newUsersLast6m ?? null)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last Year</p>
              <p className="text-2xl font-bold text-theme-text">
                {healthLoading
                  ? '...'
                  : formatNumber(health?.userActivity.newUsersLastYear ?? null)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Betting Data Stats */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">Betting Activity</h2>
          <p className="text-sm text-theme-text-tertiary">Parlay tracking and pick generation</p>
        </div>

        <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4">
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Total Parlays Tracked</p>
            <p className="text-2xl font-bold text-theme-text">
              {healthLoading
                ? '...'
                : formatNumber(health?.bettingData.totalParlaysTracked ?? null)}
            </p>
          </div>
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Tracked Parlays Pending</p>
            <p className="text-2xl font-bold text-theme-warning">
              {healthLoading
                ? '...'
                : formatNumber(health?.bettingData.parlaysPendingSettlement ?? null)}
            </p>
          </div>
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Picks Generated Today</p>
            <p className="text-2xl font-bold text-theme-primary">
              {healthLoading
                ? '...'
                : formatNumber(health?.bettingData.picksGeneratedToday ?? null)}
            </p>
          </div>
        </div>
      </div>

      {/* Settlement Status */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">Settlement Pipeline</h2>
          <p className="text-sm text-theme-text-tertiary">Bet settlement queue and status</p>
        </div>

        <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Pending Legs</p>
            <p className="text-2xl font-bold text-theme-warning">
              {healthLoading ? '...' : formatNumber(health?.settlement.pendingLegs ?? null)}
            </p>
          </div>
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Pending Parlays</p>
            <p className="text-2xl font-bold text-theme-warning">
              {healthLoading ? '...' : formatNumber(health?.settlement.pendingParlays ?? null)}
            </p>
          </div>
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Games to Settle</p>
            <p className="text-2xl font-bold text-theme-text">
              {healthLoading
                ? '...'
                : formatNumber(health?.settlement.gamesAwaitingSettlement ?? null)}
            </p>
          </div>
          <div className="bg-theme-bg rounded-lg p-4">
            <p className="text-sm text-theme-text-tertiary">Last Run</p>
            <p className="text-lg font-bold text-theme-text-secondary">
              {healthLoading
                ? '...'
                : health?.settlement.lastSettlementRun
                  ? new Date(health.settlement.lastSettlementRun).toLocaleTimeString()
                  : 'N/A'}
            </p>
          </div>
        </div>

        {!healthLoading && health?.settlementMonitor !== undefined && (
          <SettlementMonitorSection report={health.settlementMonitor} />
        )}
      </div>

      {/* LLM Usage & Cost */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">LLM Usage & Cost</h2>
          <p className="text-sm text-theme-text-tertiary">AI model usage and estimated costs</p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-theme-text-secondary mb-2">Usage by Period</h3>
          <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Today</p>
              <p className="text-2xl font-bold text-theme-primary">
                $
                {healthLoading ? '...' : (health?.llmUsage?.today?.totalCost?.toFixed(2) ?? '0.00')}
              </p>
              <p className="text-xs text-theme-text-tertiary">
                {healthLoading ? '...' : formatNumber(health?.llmUsage?.today?.calls ?? 0)} calls
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last 7 Days</p>
              <p className="text-2xl font-bold text-theme-text">
                $
                {healthLoading
                  ? '...'
                  : (health?.llmUsage?.last7d?.totalCost?.toFixed(2) ?? '0.00')}
              </p>
              <p className="text-xs text-theme-text-tertiary">
                {healthLoading ? '...' : formatNumber(health?.llmUsage?.last7d?.calls ?? 0)} calls
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last 30 Days</p>
              <p className="text-2xl font-bold text-theme-text">
                $
                {healthLoading
                  ? '...'
                  : (health?.llmUsage?.last30d?.totalCost?.toFixed(2) ?? '0.00')}
              </p>
              <p className="text-xs text-theme-text-tertiary">
                {healthLoading ? '...' : formatNumber(health?.llmUsage?.last30d?.calls ?? 0)} calls
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">All Time</p>
              <p className="text-2xl font-bold text-theme-success">
                $
                {healthLoading
                  ? '...'
                  : (health?.llmUsage?.allTime?.totalCost?.toFixed(2) ?? '0.00')}
              </p>
              <p className="text-xs text-theme-text-tertiary">
                {healthLoading ? '...' : formatNumber(health?.llmUsage?.allTime?.calls ?? 0)} calls
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-theme-text-secondary mb-1">
            Saved by Response Cache
          </h3>
          <p className="text-xs text-theme-text-tertiary mb-2">
            Each replay served earlier picks instead of calling the model; savings are what the
            original generation cost.
          </p>
          <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
            {(
              [
                ['Today', health?.llmUsage?.today],
                ['Last 7 Days', health?.llmUsage?.last7d],
                ['Last 30 Days', health?.llmUsage?.last30d],
                ['All Time', health?.llmUsage?.allTime],
              ] as const
            ).map(([label, period]) => (
              <div key={label} className="bg-theme-bg rounded-lg p-4">
                <p className="text-sm text-theme-text-tertiary">{label}</p>
                <p className="text-2xl font-bold text-theme-text">
                  ${healthLoading ? '...' : (period?.responseCache?.costSaved ?? 0).toFixed(2)}
                </p>
                <p className="text-xs text-theme-text-tertiary">
                  {healthLoading
                    ? '...'
                    : `${formatNumber(period?.responseCache?.replays ?? 0)} replays, ${formatNumber(
                        period?.responseCache?.tokensSaved ?? 0,
                      )} tokens`}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-theme-text-secondary mb-2">
            Token Usage (All Time)
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Input Tokens</p>
              <p className="text-xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.llmUsage?.allTime?.inputTokens ?? 0)}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Output Tokens</p>
              <p className="text-xl font-bold text-theme-text">
                {healthLoading ? '...' : formatNumber(health?.llmUsage?.allTime?.outputTokens ?? 0)}
              </p>
            </div>
          </div>
        </div>

        {health?.llmUsage?.byProvider && health.llmUsage.byProvider.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-theme-text-secondary mb-2">
              Usage by Provider
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border text-left">
                    <th className="py-2 px-2">Provider</th>
                    <th className="py-2 px-2 text-right">Calls</th>
                    <th className="py-2 px-2 text-right">Input Tokens</th>
                    <th className="py-2 px-2 text-right">Output Tokens</th>
                    <th className="py-2 px-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {health.llmUsage.byProvider.map((provider) => (
                    <tr key={provider.provider} className="border-b border-theme-border/50">
                      <td className="py-2 px-2 font-medium text-theme-text capitalize">
                        {provider.provider}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {formatNumber(provider.calls)}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {formatNumber(provider.inputTokens)}
                      </td>
                      <td className="py-2 px-2 text-right text-theme-text-secondary">
                        {formatNumber(provider.outputTokens)}
                      </td>
                      <td className="py-2 px-2 text-right font-medium text-theme-success">
                        ${provider.totalCost.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* A/B Testing Performance */}
      <ABTestingSection />

      {/* Leaderboard Management */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">Leaderboard Management</h2>
          <p className="text-sm text-theme-text-tertiary">
            Manually trigger leaderboard stats recomputation for all users
          </p>
        </div>

        <div>
          <button
            type="button"
            onClick={handleRecomputeLeaderboard}
            disabled={recomputing}
            className="px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill/80 text-theme-on-primary-fill rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {recomputing ? (
              <>
                <LoadingSpinner />
                Recomputing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Recompute All Stats
              </>
            )}
          </button>
          {recomputeSuccess && recomputeResult && (
            <div className="mt-3 bg-theme-success/10 border border-theme-success/20 rounded-lg p-4">
              <p className="text-sm font-medium text-theme-success mb-2">
                Recomputation completed!
              </p>
              <div className="text-xs text-theme-text-secondary space-y-1">
                <p>Total users: {recomputeResult.totalUsers}</p>
                <p>Successful: {recomputeResult.successCount}</p>
                {recomputeResult.errorCount > 0 && (
                  <p className="text-theme-warning">Errors: {recomputeResult.errorCount}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Historical Efficiency */}
      <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">Historical Efficiency</h2>
          <p className="text-sm text-theme-text-tertiary">
            Recalculate historical efficiency data used for AI pick feedback loop
          </p>
        </div>

        <div>
          <button
            type="button"
            onClick={handleRecalculateEfficiency}
            disabled={recalculating}
            className="px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill/80 text-theme-on-primary-fill rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {recalculating ? (
              <>
                <LoadingSpinner />
                Recalculating...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Recalculate Historical Data
              </>
            )}
          </button>
          {recalcResult && (
            <div className="mt-3 bg-theme-success/10 border border-theme-success/20 rounded-lg p-4">
              <p className="text-sm font-medium text-theme-success mb-2">Recalculation complete!</p>
              <div className="text-xs text-theme-text-secondary space-y-1">
                <p>Total settled legs: {recalcResult.globalLegsCount.toLocaleString()}</p>
                <p>After deduplication: {recalcResult.dedupedCount.toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
